import * as React from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import useAuth from '@/src/Authentication/components/hooks/useAuth';
import '../v2/tokens.css';

/*
 * Crew portal frame — a lightweight peer of V2Frame for contractors.
 * Gate: contractor role (3); admins/global admins pass for support.
 * Chrome: topbar + icon-only dock (Gigs · Profile), mobile-first.
 */

const CONTRACTOR_ROLE = 3;
const PASS_ROLES = [0, 69];

export default function CrewFrame() {
    const { auth, signout } = useAuth() as any;
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const roles: number[] = auth?.roles ?? [];

    if (
        !roles.includes(CONTRACTOR_ROLE) &&
        !roles.some((r) => PASS_ROLES.includes(r))
    ) {
        return <Navigate to="/login" replace />;
    }

    const item = (
        key: string,
        title: string,
        active: boolean,
        onClick: () => void,
        icon: React.ReactNode,
    ) => (
        <button
            key={key}
            type="button"
            title={title}
            onClick={onClick}
            className={
                'flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border-0 font-[inherit] ' +
                (active
                    ? 'bg-brand text-white'
                    : 'bg-transparent text-dock-dim hover:bg-dock-hover hover:text-dock-fg')
            }
        >
            {icon}
        </button>
    );

    return (
        <div className="psv2 min-h-screen bg-background pb-[110px] text-foreground">
            <div className="sticky top-0 z-20 mx-auto flex max-w-[430px] items-center justify-between px-5 py-3.5 backdrop-blur-md">
                <div className="flex items-center gap-2.5">
                    <span className="flex h-[25px] w-[25px] items-center justify-center rounded-full bg-foreground">
                        <span className="h-[8.5px] w-[8.5px] rounded-full bg-background" />
                    </span>
                    <span className="text-[13.5px] font-semibold">
                        Smilebooth Crew
                    </span>
                </div>
                <button
                    type="button"
                    onClick={async () => {
                        await signout?.().catch(() => null);
                        navigate('/login');
                    }}
                    className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12px] font-semibold text-faint hover:text-foreground"
                >
                    Log out
                </button>
            </div>

            <div className="mx-auto max-w-[430px] px-5">
                <Outlet />
            </div>

            {/* icon-only dock (no text in bottom nav) */}
            <div className="fixed bottom-[22px] left-1/2 z-30 flex -translate-x-1/2 items-center gap-0.5 rounded-full bg-dock p-[7px] shadow-[0_10px_34px_-6px_rgba(0,0,0,0.55)]">
                {item(
                    'gigs',
                    'Gigs',
                    pathname === '/crew',
                    () => navigate('/crew'),
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="14" height="13" rx="2" />
                        <path d="M3 8h14M7 2.5V5.5M13 2.5V5.5" />
                    </svg>,
                )}
                {item(
                    'profile',
                    'Profile',
                    pathname === '/crew/profile',
                    () => navigate('/crew/profile'),
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="10" cy="6.5" r="3" />
                        <path d="M4 17v-1.5a4.5 4.5 0 0 1 4.5-4.5h3A4.5 4.5 0 0 1 16 15.5V17" />
                    </svg>,
                )}
            </div>
        </div>
    );
}
